import type { ReactNode } from 'react';
import { CountUp } from '@/components/CountUp.tsx';
import { GradientText, SplitText } from '@/components/GradientText.tsx';
import { Img } from '@/components/Img.tsx';
import { Layout } from '@/components/Layout.tsx';
import { PageHero } from '@/components/PageHero.tsx';
import {
  ButtonLink,
  Divider,
  Eyebrow,
  Icons,
  Quote,
  SectionIntro,
} from '@/components/ui.tsx';
import { QUOTES } from '@/data/site.ts';
import { href } from '@/lib/paths.ts';

const SOLUTION_CARDS: readonly {
  n: string;
  title: string;
  body: string;
  art: ReactNode;
}[] = [
  {
    n: '01',
    title: 'Tax Equity Offset',
    body: 'Strategically invest into energy programs that generate Investment Tax Credits, directly reducing your federal tax liability by up to 40%. Our programs are structured to maximize returns while maintaining full compliance.',
    art: (
      <svg
        width="120"
        height="120"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="0.5"
        aria-hidden="true"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    n: '02',
    title: 'Passive Income Generation',
    body: 'Beyond the immediate cash savings, our energy investments produce ongoing revenue streams. As demand for power surges in an AI-driven world, your investments are positioned for long-term growth and passive income.',
    art: (
      <svg
        width="120"
        height="120"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="0.5"
        aria-hidden="true"
      >
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
  },
  {
    n: '03',
    title: 'Real-Time Portfolio Monitoring',
    body: "Our proprietary technology platform allows you to see your investment with ROI in real time. Lux's tech platform far exceeds those of competitors, giving you complete transparency into your portfolio performance.",
    art: (
      <svg
        width="120"
        height="120"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="0.5"
        aria-hidden="true"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    n: '04',
    title: 'Up to 40%+ Cash Savings',
    body: 'Through our comprehensive approach combining ITC benefits with strategic program selection, clients can achieve up to 40%+ in total cash savings, transforming significant tax burdens into wealth-building opportunities.',
    art: (
      <svg
        width="120"
        height="120"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="0.5"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M16 8l-4 4-4-4M8 16l4-4 4 4" />
      </svg>
    ),
  },
];

const BENEFITS = [
  {
    icon: Icons.badgeCheck,
    title: 'Seamless Integration',
    body: "Our team works directly with you and your clients' existing advisors to integrate tax equity solutions without disrupting current strategies.",
  },
  {
    icon: Icons.shieldSmall,
    title: 'Transparency First',
    body: 'Full visibility into every investment, return, and tax benefit. Our technology platform gives you and your clients real-time access to portfolio performance.',
  },
  {
    icon: Icons.usersSmall,
    title: 'Collaborative Culture',
    body: 'We put clients and their advisors first. Our approach ensures you remain the trusted advisor while we provide specialized tax equity expertise.',
  },
] as const;

export function Solutions() {
  return (
    <Layout currentPath="/solutions">
      <PageHero
        image="images/hero-solutions.webp"
        imageAlt=""
        width={1920}
        height={1278}
        eyebrow="Tax Solutions & Advisor Partnerships"
        heading={
          <>
            Investment Tax Credit{' '}
            <GradientText as="em" immediate startDelay={0.5}>
              Solutions
            </GradientText>
          </>
        }
        lead="In the future, power isn't just electricity. It's currency. Turn your taxes into income with Lux Financial's energy investment programs."
      />

      {/* ===== ITC ===== */}
      <section className="section-pad">
        <div className="container">
          <div className="split">
            <div className="split__image fade-in">
              <Img
                src="images/solutions-analysis.webp"
                alt="Hand using a digital stylus to annotate financial charts"
                width={1200}
                height={900}
                sizes="(max-width: 900px) 100vw, 50vw"
              />
            </div>
            <div className="split__content fade-in fade-in-delay-1">
              <Eyebrow>Investment Tax Credit</Eyebrow>
              <h2>
                Up to <CountUp value={40} suffix="%+" /> in{' '}
                <GradientText as="em" startDelay={0.5}>
                  Cash Savings
                </GradientText>
              </h2>
              <p className="lead">
                Lux Financial helps high net worth individuals and their
                companies obtain up to 40%+ in cash savings from the Investment
                Tax Credit (ITC), one of the most powerful tools available for
                reducing federal tax liability.{' '}
                <a href={href('/article-tax-equity')} className="inline-link">
                  Read the full breakdown
                </a>
                .
              </p>
              <Divider />
              <p className="lead">
                Our energy programs are designed to not only address the growing
                demand for power but also give investors the opportunity to
                participate in what we believe will become one of the
                world&rsquo;s most valuable currencies: energy.
              </p>
              <ButtonLink to="/#contact" className="mt-2">
                Schedule a Call
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section--forest section-pad">
        <div className="container">
          <SectionIntro>
            <Eyebrow>For Investors</Eyebrow>
            <h2>How It Works</h2>
          </SectionIntro>
          <ol className="grid grid-cols-1 gap-8 md:grid-cols-2 list-none p-0 m-0">
            {SOLUTION_CARDS.map((card, i) => (
              <li
                key={card.n}
                className={`solution-card fade-in ${i % 2 === 1 ? 'fade-in-delay-1' : ''}`.trim()}
              >
                <div>
                  <div className="solution-card__number" aria-hidden="true">
                    {card.n}
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </div>
                <div className="solution-card__art">{card.art}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <Quote {...QUOTES.cfoEnergy} />

      {/* ===== For advisors ===== */}
      <section className="section-pad">
        <div className="container">
          <div className="split split--reverse">
            <div className="split__image split__image--device fade-in">
              <Img
                src="images/advisor-article-iphone.webp"
                alt="Apple News article on an iPhone: Lux Financial — why investors, CPAs, and family offices should know their name"
                width={1132}
                height={1200}
                sizes="(max-width: 900px) 100vw, 50vw"
              />
            </div>
            <div className="split__content fade-in fade-in-delay-1">
              <Eyebrow>Learn Why CPAs Choose Us</Eyebrow>
              <h2>
                We Help You Help{' '}
                <GradientText as="em" startDelay={0.5}>
                  Your Clients
                </GradientText>
              </h2>
              <p className="lead">
                Lux partners with CPAs, wealth advisors, and family offices to
                reduce client tax liability and create passive income
                opportunities. We&rsquo;ve built a culture centered on
                collaboration, working hand-in-hand with your practice.
              </p>
              <Divider />
              {BENEFITS.map((b) => (
                <div className="benefit" key={b.title}>
                  <div className="benefit__icon">{b.icon}</div>
                  <div>
                    <h4>{b.title}</h4>
                    <p>{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Quote {...QUOTES.presidentPlanning} background="var(--surface-3)" />

      {/* ===== CTA ===== */}
      <section className="cta-section section-pad">
        <div className="container">
          <Eyebrow className="fade-in">Get Started</Eyebrow>
          <h2 className="fade-in fade-in-delay-1">
            <SplitText>Want More Info?</SplitText>
          </h2>
          <p className="lead fade-in fade-in-delay-2">
            Hop on a quick call with us. A team member will reach out within
            24&ndash;48 hours.
          </p>
          <div className="cta-buttons fade-in fade-in-delay-3">
            <ButtonLink to="/#contact">Schedule a Call</ButtonLink>
            <ButtonLink to="/about">Meet Our Team</ButtonLink>
          </div>
        </div>
      </section>
    </Layout>
  );
}
